import { useEffect } from 'react';

const SEO_TITLE = 'DocCarePlus | Clinic Management, Queue, Billing, and Patient Portal Software';
const SEO_DESCRIPTION =
  'DocCarePlus is a modern healthcare SaaS platform for clinic appointments, smart queues, billing, role-based access control, multi-clinic management, and branded patient portals.';
const SEO_URL = 'https://doccareplus.com/';
// Social scrapers (WhatsApp/LinkedIn/Slack/X) cannot render SVG and require an
// absolute URL — must be a raster asset, not /long_logo.svg.
const SEO_IMAGE = 'https://doccareplus.com/dashboard-mockup.png';
const JSON_LD_ID = 'doccareplus-landing-jsonld';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'DocCarePlus',
  applicationCategory: 'HealthApplication',
  operatingSystem: 'Web',
  url: SEO_URL,
  description: SEO_DESCRIPTION,
  offers: {
    '@type': 'Offer',
    price: '49',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
  },
  featureList: [
    'Smart appointment scheduling',
    'Live queue management',
    'Billing and invoice management',
    'Role-based access control',
    'Multi-clinic management',
    'White-label patient portals',
  ],
  publisher: {
    '@type': 'Organization',
    name: 'DocCarePlus',
    url: 'https://doccareplus.com',
  },
};

function upsertMeta(selector: string, create: () => HTMLMetaElement | HTMLLinkElement, apply: (element: HTMLMetaElement | HTMLLinkElement) => void) {
  let element = document.head.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  apply(element);
}

export function LandingSeo() {
  useEffect(() => {

    upsertMeta('meta[name="description"]', () => {
      const element = document.createElement('meta');
      element.setAttribute('name', 'description');
      return element;
    }, (element) => element.setAttribute('content', SEO_DESCRIPTION));

    upsertMeta('link[rel="canonical"]', () => {
      const element = document.createElement('link');
      element.setAttribute('rel', 'canonical');
      return element;
    }, (element) => element.setAttribute('href', SEO_URL));

    const meta = [
      ['property', 'og:title', SEO_TITLE],
      ['property', 'og:description', SEO_DESCRIPTION],
      ['property', 'og:type', 'website'],
      ['property', 'og:url', SEO_URL],
      ['property', 'og:image', SEO_IMAGE],
      ['name', 'twitter:card', 'summary_large_image'],
      ['name', 'twitter:title', SEO_TITLE],
      ['name', 'twitter:description', SEO_DESCRIPTION],
      ['name', 'twitter:image', SEO_IMAGE],
    ] as const;

    meta.forEach(([attribute, key, value]) => {
      upsertMeta(`meta[${attribute}="${key}"]`, () => {
        const element = document.createElement('meta');
        element.setAttribute(attribute, key);
        return element;
      }, (element) => element.setAttribute('content', value));
    });

    let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = JSON_LD_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
  }, []);

  return null;
}
